import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useTransition, animated } from 'react-spring';
import { Form, FormControl } from 'react-bootstrap';
import classNames from 'classnames';
import toastr from '../toastr';
import BodyPane from './BodyPane';
import FlatButton from './FlatButton';
import Footer from './Footer';
import PageContainer from '../PageContainer';
import PartPane from './PartPane';
import PurchaseOrderPane from './PurchaseOrderPane';
import QuantityPane from './QuantityPane';
import Review from './Review';
import '../../styles/scanner.scss';
import ApiService from '../../services/ApiService';
import FileService from '../../services/FileService';

const defaultListItem = () => {
  return { part: '', qty: 1, po: '', done: false, packingSlip: '' };
};

const BarcodeReceiving = () => {
  const [list, setList] = useState([defaultListItem()]);
  const [listIndex, setListIndex] = useState(0);
  const [polist, setPolist] = useState([]);
  const [review, setReview] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [threshold, setThreshold] = useState(null);
  const [submitting, setSubmitting] = useState(false); // eslint-disable-line
  const [packingSlip, setPackingSlip] = useState('');

  const steps = [
    { name: 'part', label: 'Choose Part', icon: 'wrench' },
    { name: 'po', label: 'Purchase Order', icon: 'money-box' },
    { name: 'qty', label: 'Quantity', icon: 'plus-circle-o-duplicate' },
  ];

  // Animations between steps
  const sectionTransitions = useTransition(currentStep, p => p, {
    from: { opacity: 0, transform: 'translate3d(100%,0,0)' },
    enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
    leave: { opacity: 0, transform: 'translate3d(-50%,0,0)' },
  });

  // Animation for review screen
  const reviewTransitions = useTransition(review, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  // Helpers
  const currentItem = list[listIndex];
  const doneCount = list.filter(item => item.done).length;
  const onLastStep = currentStep === steps.length - 1;
  const onFirstStep = currentStep === 0;

  const isUnique = (part, po) => {
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      if (item.po && item.part) {
        if (item.po === po && item.part === part) {
          return false;
        }
      }
    }

    return true;
  };

  const switchTo = toStep => {
    setCurrentStep(toStep);
  };

  const next = () => {
    // On last section
    if (onLastStep) {
      if (currentItem.done) {
        if (!list[listIndex + 1]) {
          setList([...list, defaultListItem()]);
          setListIndex(listIndex + 1);
        } else {
          setListIndex(listIndex + 1);
        }
        setCurrentStep(0);
        return;
      }
    }

    setCurrentStep(prevSection => prevSection + 1);
  };

  const back = () => {
    if (!onFirstStep) {
      setCurrentStep(prevSection => prevSection - 1);
    }
  };

  const updatePart = newPart => {
    const newList = list.map((item, index) => {
      if (index !== listIndex) {
        return item;
      }

      if (item.part === newPart) {
        return item;
      }

      return {
        ...item,
        part: newPart,
        po: '', // Reset PO when part is set
        done: false,
        packingSlip,
      };
    });

    setList(newList);
    next();
  };

  const updateQuantity = newQuantity => {
    const newList = list.map((item, index) => {
      if (index !== listIndex) {
        return item;
      }

      let done = false;
      if (item.part && item.po) {
        done = true;
      }

      return {
        ...item,
        qty: newQuantity,
        packingSlip,
        done,
      };
    });

    setList(newList);
  };

  const selectPO = (newPO, threshold) => {
    const newList = list.map((item, index) => {
      if (index !== listIndex) {
        return item;
      }

      let done = false;
      if (item.part && newPO) {
        done = true;
      }

      return {
        ...item,
        po: newPO,
        qty: threshold,
        packingSlip,
        done,
      };
    });

    setList(newList);
    setThreshold(threshold);
    next();
  };

  const submit = () => {
    setSubmitting(true);
    const done = list.filter(item => item.done);

    ApiService.put('/item/PBM1', done)
      .then(response => {
        setSubmitting(false);
        setListIndex(0);
        setList([defaultListItem()]);
        setCurrentStep(0);
        setReview(false);

        const file = _.get(response, 'data.responses[0].file[0]');
        if (file) {
          FileService.downloadPath(file.filename, file.source);
        }

        const message = `There were ${done.length} items submitted.`;

        toastr.success('Submitted', message);
      })
      .catch(error => {
        const response = error.response;
        let message = 'An error occurred.';

        const responses = _.get(response, 'data.responses');

        // Concatenate errors
        if (responses && Array.isArray(responses)) {
          message = '';
          responses.forEach(error => {
            message = `${message}${error.error} `;
          });
        }

        toastr.error('Error', message);
      });
  };

  const renderBottomToolbar = () => {
    const buttons = [];

    if (!onFirstStep) {
      buttons.push(
        <FlatButton key="back" onClick={back} icon="long-arrow-left" iconAlign="left">
          Back
        </FlatButton>
      );
    }

    if (onFirstStep && currentItem.part === '' && list.length > 1) {
      buttons.push(
        <FlatButton key="review" onClick={() => setReview(true)} className="review" icon="view-list" iconAlign="left">
          Review
        </FlatButton>
      );
    }

    const nextButton = (
      <FlatButton key="next" className="next" onClick={next} icon="long-arrow-right">
        Next
      </FlatButton>
    );

    const stepName = steps[currentStep].name;

    if (!onLastStep) {
      if (currentItem[stepName] !== '') {
        buttons.push(nextButton);
      }
    } else {
      if (currentItem[stepName] !== '' && currentItem.done === true) {
        buttons.push(nextButton);
      }
    }

    return buttons;
  };

  const getOpenIndex = () => {
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].done === false) {
        return i;
      }
    }

    setList([...list, defaultListItem()]);
    return list.length;
  };

  const edit = (index = 0, section = 0) => {
    if (index === 'last') {
      setListIndex(getOpenIndex());
      setCurrentStep(0);
      setReview(false);
      return;
    }

    setCurrentStep(section);
    setListIndex(index);
    setReview(false);
  };

  const remove = removeIndex => {
    if (removeIndex === undefined) {
      return;
    }

    const newList = list.filter((item, index) => {
      return removeIndex !== index;
    });

    setList(newList);

    if (!newList[listIndex]) {
      setListIndex(newList.length - 1);
      setCurrentStep(0);
    }
  };

  const partValidationCallback = (partId, setMessage) => {
    // Find POs for a given Part Number
    return ApiService.get(`part/${encodeURIComponent(partId)}/POs`)
      .then(response => {
        const pos = !Array.isArray(response.data) ? [] : response.data;
        if (!pos.length) {
          setMessage('No Purchase Orders associated with part');
        } else {
          setPolist(pos);
        }
      })
      .catch(() => {
        setMessage('There was a problem fetching Purchase Orders');
      });
  };

  const handleSlipBlur = () => {
    const newList = list.map((item, index) => {
      if (index !== listIndex) {
        return item;
      }

      return {
        ...item,
        packingSlip,
      };
    });

    setList(newList);
  };

  useEffect(() => {
    if (doneCount === 0) {
      setListIndex(list.length - 1);
      setReview(false);
    }
  }, [list]);

  useEffect(() => {
    if (currentItem.po && currentItem.part) {
      polist.forEach(po => {
        if (po.id === currentItem.po) {
          setThreshold(po.qty);
        }
      });
    } else {
      setThreshold(null);
    }
  }, [currentStep]);

  const renderCurrentInfo = () => {
    if (!currentItem.part || onFirstStep) {
      return null;
    }

    return (
      <div className="current-item">
        <div className="set-item-label">Current Part</div>
        <div className="set-item-value">{currentItem.part}</div>
      </div>
    );
  };

  const renderTopToolbar = () => {
    const toolbar = [];

    steps.forEach((step, index) => {
      const active = currentStep === index;
      const className = classNames({
        active,
      });

      toolbar.push(
        <FlatButton
          className={className}
          icon={step.icon}
          iconAlign="left"
          onClick={() => switchTo(index)}
          key={step.name}
        >
          {active === true && step.label}
        </FlatButton>
      );
    });

    return toolbar;
  };

  const sections = [
    (
      { style } // eslint-disable-line
    ) => (
      <animated.div style={style} className="scan-form scan-part">
        <PartPane
          initialValue={currentItem.part}
          updateValue={updatePart}
          validationCallback={partValidationCallback}
        />
      </animated.div>
    ),
    (
      { style } // eslint-disable-line
    ) => (
      <animated.div style={style} className="scan-form scan-po">
        <PurchaseOrderPane
          initialPO={currentItem.po}
          isAvailable={po => isUnique(currentItem.part, po)}
          list={polist}
          onChange={selectPO}
        />
      </animated.div>
    ),
    (
      { style } // eslint-disable-line
    ) => (
      <animated.div style={style} className="scan-form scan-quantity">
        <QuantityPane initialQty={currentItem.qty} onChange={updateQuantity} threshold={threshold} />
      </animated.div>
    ),
  ];

  const toolbar = renderBottomToolbar();

  if (review) {
    return (
      <PageContainer disableSidebar>
        {reviewTransitions.map(
          ({ item, key, props }) =>
            item && (
              <animated.div className="scanner" key={key} style={props}>
                <Review edit={edit} remove={remove} list={list} submit={submit} />
              </animated.div>
            )
        )}
      </PageContainer>
    );
  }

  return (
    <PageContainer disableSidebar>
      <div className="scanner">
        <div className={classNames('scan-head', { 'no-part': !currentItem.part })}>{renderTopToolbar()}</div>
        <BodyPane sections={sections} transitions={sectionTransitions} step={currentStep} />
        {renderCurrentInfo()}
        <Footer>
          {doneCount !== 0 && (
            <FlatButton className="review mr-2" icon="view-list" iconAlign="left" onClick={() => setReview(true)}>
              {doneCount}
            </FlatButton>
          )}

          <Form inline className='mt-1 float-left'>
            <i className="fas fa-box-open mr-1"></i>
            <FormControl
              placeholder="Packing Slip"
              onChange={e => setPackingSlip(e.target.value)}
              onBlur={handleSlipBlur}
              style={{ paddingLeft: '5px', backgroundColor: 'var(--yellow-0)' }}
              value={packingSlip}
            />
          </Form>

          <div className="scan-toolbar">{toolbar}</div>
        </Footer>
        <div className="invis-footer" />
      </div>
    </PageContainer>
  );
};

export default BarcodeReceiving;
